/**
 * Paid-traffic gate for Meta tracking.
 *
 * We only report to Meta (browser Pixel + server CAPI) for *paid* ad
 * clicks. Organic / direct / internal-team visits are excluded so
 * internal QA never pollutes the Pixel or the conversion signal the
 * campaign optimises on.
 *
 * Classification mirrors the Worker's `trafficTypeSql`
 * (apps/worker/src/lib/traffic.ts) so client-side gating and the admin
 * dashboard's paid/organic split agree:
 *   paid = an ad-platform `utm_medium` (cpc / paid / paid_social / …)
 *          OR a Google/Bing/TikTok click-id (gclid/msclkid/ttclid) in
 *          the landing URL.
 * An `fbclid` ALONE is NOT treated as paid — Meta appends it to organic
 * Facebook/Instagram clicks too, so it would over-report. Our paid ads
 * tag `utm_medium=paid`, which this catches.
 *
 * The decision is computed once — at the funnel landing, where the URL
 * still carries the utm / click-id — and cached in sessionStorage so it
 * stays stable across the whole session, including the post-Paddle
 * `/success` page whose URL has no attribution params.
 */

const CACHE_KEY = "h2a:capi-paid";

const PAID_MEDIUMS = new Set([
  "cpc",
  "ppc",
  "paid",
  "paidsocial",
  "paid_social",
  "paid-social",
  "display",
  "cpm",
  "paidsearch",
  "paid_search",
  "social_paid",
]);

function computeFromUrl(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const search = window.location.search.toLowerCase();
    if (
      search.includes("gclid=") ||
      search.includes("msclkid=") ||
      search.includes("ttclid=")
    ) {
      return true;
    }
    const medium = (
      new URLSearchParams(window.location.search).get("utm_medium") ?? ""
    )
      .toLowerCase()
      .trim();
    return PAID_MEDIUMS.has(medium);
  } catch {
    return false;
  }
}

/**
 * True when the current session originated from a paid ad click. Cached
 * after the first call so the landing-URL decision survives navigation
 * to attribution-less pages (e.g. `/success` after Paddle's redirect).
 */
export function isPaidTraffic(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached === "1") return true;
    if (cached === "0") return false;
  } catch {
    /* private mode — recompute each call (still correct mid-funnel) */
  }
  const paid = computeFromUrl();
  try {
    sessionStorage.setItem(CACHE_KEY, paid ? "1" : "0");
  } catch {
    /* ignore */
  }
  return paid;
}
